import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";

/**
 * Minimal public chrome (no auth, no app shell) for stand-alone marketing-style
 * pages like Contact and Status. Mirrors BlogChrome's design language so these
 * pages read as part of the product, with a footer that cross-links the public
 * surfaces (home, status, contact, privacy).
 */
export function PublicShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col bg-surface-0 text-ink">
      <header className="sticky top-0 z-30 border-b border-line bg-surface-0/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="ring-focus rounded-md">
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link
              to="/app"
              className="ring-focus inline-flex items-center whitespace-nowrap rounded-md bg-brand px-3.5 py-1.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-brand-dim"
            >
              {t("public.openApp")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-line bg-surface-1">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-6 text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>© {new Date().getFullYear()} Biedradar</span>
          <nav className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/" className="ring-focus rounded hover:text-ink">{t("public.footer.home")}</Link>
            <Link to="/status" className="ring-focus rounded hover:text-ink">{t("public.footer.status")}</Link>
            <Link to="/contact" className="ring-focus rounded hover:text-ink">{t("public.footer.contact")}</Link>
            <Link to="/privacy" className="ring-focus rounded hover:text-ink">{t("public.footer.privacy")}</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
